import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicFormPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({});

  // Capture tracking params from URL
  const trackingData = {
    affiliate_id: searchParams.get('ref') || searchParams.get('affiliate_id') || '',
    referral_code: searchParams.get('ref') || '',
    sub_ref: searchParams.get('sub_ref') || '',
    utm_source: searchParams.get('utm_source') || '',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || '',
    utm_content: searchParams.get('utm_content') || '',
    fbclid: searchParams.get('fbclid') || '',
    gclid: searchParams.get('gclid') || '',
    product_hint: searchParams.get('product') || '',
    landing_page: window.location.href,
    referrer: document.referrer || '',
    form_slug: slug,
    page_name: document.title || '',
    session_id: getOrCreateSessionId(),
  };

  useEffect(() => {
    // Store tracking in localStorage for 30 days persistence
    const existing = JSON.parse(localStorage.getItem('_isocntrl_tracking') || '{}');
    const merged = { ...existing };
    Object.entries(trackingData).forEach(([k, v]) => {
      if (v && !merged[k]) merged[k] = v; // First touch
    });
    merged._stored_at = Date.now();
    localStorage.setItem('_isocntrl_tracking', JSON.stringify(merged));

    // Fire click tracking if affiliate present
    if (trackingData.affiliate_id) {
      fetch(`${API_BASE}/api/tracking/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackingData),
      }).catch(() => {});
    }
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadForm = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await fetch(`${API_BASE}/api/forms/public/${slug}`);
      if (!resp.ok) throw new Error('Form not found');
      const data = await resp.json();
      setForm(data);
      // Pre-populate defaults
      const defaults = {};
      (data.fields || []).forEach(f => {
        if (f.default_value) defaults[f.name] = f.default_value;
      });
      setFormData(defaults);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { loadForm(); }, [loadForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate required fields
    const missingFields = (form.fields || []).filter(f => f.required && !formData[f.name]?.toString().trim());
    if (missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      setSubmitting(false);
      return;
    }

    // Merge stored tracking data with current
    const storedTracking = JSON.parse(localStorage.getItem('_isocntrl_tracking') || '{}');
    const payload = {
      ...formData,
      ...storedTracking,
      ...trackingData,
      form_slug: slug,
      source_type: 'form',
      source_detail: slug,
      monthly_volume: formData.monthly_volume ? parseFloat(formData.monthly_volume) : undefined,
    };

    try {
      const resp = await fetch(`${API_BASE}/api/leads/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Submission failed');
      setResult(data);
      setSubmitted(true);

      // Handle redirect
      const redirectUrl = data.redirect_url || data.application_url;
      if (redirectUrl) {
        setTimeout(() => { window.location.href = redirectUrl; }, 2000);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-500 mb-4" />
          <h2 className="text-lg font-semibold mb-2">Form Not Available</h2>
          <p className="text-sm text-gray-600">{error}</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <CheckCircle2 className="h-12 w-12 mx-auto text-green-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">{form?.success_message || 'Thank you!'}</h2>
          {result?.product && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Recommended: <strong>{result.product}</strong>
                {result.hardware_recommendation && <> — {result.hardware_recommendation}</>}
              </p>
            </div>
          )}
          {(result?.redirect_url || result?.application_url) && (
            <p className="text-sm text-gray-500 mt-3">Redirecting you shortly...</p>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <Card className="p-6 sm:p-8" data-testid="public-form">
          <div className="mb-6">
            <h1 className="text-xl font-bold">{form.name}</h1>
            {form.description && <p className="text-sm text-gray-600 mt-1">{form.description}</p>}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {(form.fields || [])
              .filter(f => f.field_type !== 'hidden')
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(field => (
                <FormField
                  key={field.field_id}
                  field={field}
                  value={formData[field.name] || ''}
                  onChange={val => setFormData(prev => ({ ...prev, [field.name]: val }))}
                />
              ))}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-form-btn">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : 'Submit Application'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

function FormField({ field, value, onChange }) {
  const label = field.label + (field.required ? ' *' : '');

  switch (field.field_type) {
    case 'textarea':
      return (
        <div>
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <textarea
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={3}
            data-testid={`field-${field.name}`}
          />
        </div>
      );
    case 'dropdown':
      return (
        <div>
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="mt-1" data-testid={`field-${field.name}`}>
              <SelectValue placeholder={field.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case 'checkbox':
      return (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value === 'true' || value === true}
            onChange={e => onChange(e.target.checked ? 'true' : 'false')}
            className="rounded"
            data-testid={`field-${field.name}`}
          />
          <label className="text-sm font-medium text-gray-700">{field.label}</label>
        </div>
      );
    case 'radio':
      return (
        <div>
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <div className="flex flex-wrap gap-3 mt-1">
            {(field.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-1.5 text-sm">
                <input type="radio" name={field.name} value={opt} checked={value === opt} onChange={() => onChange(opt)} />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );
    default:
      return (
        <div>
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <Input
            type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="mt-1"
            data-testid={`field-${field.name}`}
          />
        </div>
      );
  }
}

function getOrCreateSessionId() {
  let sid = localStorage.getItem('_isocntrl_session');
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('_isocntrl_session', sid);
  }
  return sid;
}
